// Hook personalizado para gestión de canales de Supabase Realtime.
// Previene fugas de memoria al manejar automáticamente el cleanup.
// Proporciona una API simplificada para suscripciones a eventos.
// Adaptado para web desde amarsi-app.

import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook para gestionar canales de Supabase Realtime de forma segura
 * @param {string} channelName - Nombre único del canal
 * @param {object} config - Configuración opcional del canal
 * @returns {object} - Objeto con channel, subscribe, on y cleanup
 */
export const useRealtimeChannel = (channelName, config = {}) => {
  const channelRef = useRef(null);
  const isSubscribedRef = useRef(false);
  const configRef = useRef(config);

  // Actualizar configuración si cambia
  useEffect(() => {
    configRef.current = config;
  }, [JSON.stringify(config)]);

  useEffect(() => {
    const DEBUG = process.env.NODE_ENV === 'development';
    
    if (DEBUG) {
      console.log('=== useRealtimeChannel: INICIANDO EFFECT ===');
      console.log('useRealtimeChannel: channelName:', channelName);
      console.log('useRealtimeChannel: config:', configRef.current);
      console.log('useRealtimeChannel: Timestamp:', new Date().toISOString());
    }
    
    // Limpiar canal anterior si existe para prevenir fugas
    if (channelRef.current) {
      if (DEBUG) {
        console.log('useRealtimeChannel: Limpiando canal anterior:', channelRef.current);
      }
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
      isSubscribedRef.current = false;
    }

    // Crear nuevo canal solo si hay un nombre válido
    if (!channelName) {
      console.warn('useRealtimeChannel: channelName es requerido');
      return;
    }

    if (DEBUG) {
      console.log('useRealtimeChannel: Creando nuevo canal:', channelName);
      console.log('useRealtimeChannel: Con configuración:', configRef.current);
    }
    
    channelRef.current = supabase.channel(channelName, configRef.current);
    
    if (DEBUG) {
      console.log('useRealtimeChannel: Canal creado exitosamente');
      console.log('useRealtimeChannel: Channel ref:', channelRef.current);
    }

    // Función de cleanup automática
    const cleanup = () => {
      if (DEBUG) {
        console.log('=== useRealtimeChannel: EJECUTANDO CLEANUP ===');
        console.log('useRealtimeChannel: Canal a limpiar:', channelName);
        console.log('useRealtimeChannel: Channel ref existe:', !!channelRef.current);
        console.log('useRealtimeChannel: Está suscrito:', isSubscribedRef.current);
        console.log('useRealtimeChannel: Timestamp:', new Date().toISOString());
      }
      
      if (channelRef.current && isSubscribedRef.current) {
        if (DEBUG) {
          console.log('useRealtimeChannel: Removiendo canal de Supabase');
        }
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        isSubscribedRef.current = false;
        
        if (DEBUG) {
          console.log('useRealtimeChannel: Canal removido exitosamente');
        }
      } else {
        if (DEBUG) {
          console.log('useRealtimeChannel: No se necesita remover canal - condiciones no cumplidas');
          console.log('  - channelRef.current existe:', !!channelRef.current);
          console.log('  - isSubscribedRef.current:', isSubscribedRef.current);
        }
      }
    };

    return cleanup;

  }, [channelName, JSON.stringify(config)]);

  // Función para suscribirse al canal
  const subscribe = useCallback((callback) => {
    const DEBUG = process.env.NODE_ENV === 'development';
    
    if (DEBUG) {
      console.log('=== useRealtimeChannel: INICIANDO SUSCRIPCIÓN ===');
      console.log('useRealtimeChannel: Canal:', channelName);
      console.log('useRealtimeChannel: Channel ref existe:', !!channelRef.current);
      console.log('useRealtimeChannel: Ya suscrito:', isSubscribedRef.current);
      console.log('useRealtimeChannel: Timestamp:', new Date().toISOString());
    }
    
    if (!channelRef.current) {
      console.warn('useRealtimeChannel: No hay canal para suscribirse');
      return;
    }

    if (isSubscribedRef.current) {
      console.warn('useRealtimeChannel: Canal ya está suscrito');
      return;
    }

    if (DEBUG) {
      console.log('useRealtimeChannel: Ejecutando subscribe() en el canal');
    }
    
    channelRef.current.subscribe((status, err) => {
      if (DEBUG) {
        console.log('=== useRealtimeChannel: ESTADO DE SUSCRIPCIÓN RECIBIDO ===');
        console.log('useRealtimeChannel: Canal:', channelName);
        console.log('useRealtimeChannel: Status:', status);
        console.log('useRealtimeChannel: Error:', err);
        console.log('useRealtimeChannel: Timestamp:', new Date().toISOString());
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ useRealtimeChannel: CANAL SUSCRITO EXITOSAMENTE');
        } else if (status === 'CHANNEL_ERROR') {
          console.log('❌ useRealtimeChannel: ERROR EN EL CANAL');
        } else if (status === 'TIMED_OUT') {
          console.log('⏰ useRealtimeChannel: TIEMPO DE ESPERA AGOTADO');
        } else if (status === 'CLOSED') {
          console.log('🔒 useRealtimeChannel: CANAL CERRADO');
        }
      }
      
      if (status === 'SUBSCRIBED') {
        isSubscribedRef.current = true;
        if (DEBUG) {
          console.log('useRealtimeChannel: Actualizando isSubscribedRef a true');
        }
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`useRealtimeChannel: Error en canal ${channelName}:`, err);
      }
      
      if (callback) {
        if (DEBUG) {
          console.log('useRealtimeChannel: Ejecutando callback con status:', status);
        }
        callback(status, err);
      }
    });
  }, [channelName]);

  // Función para agregar listeners de eventos
  const on = useCallback((event, filter, callback) => {
    const DEBUG = process.env.NODE_ENV === 'development';
    
    if (DEBUG) {
      console.log('=== useRealtimeChannel: AGREGANDO LISTENER ===');
      console.log('useRealtimeChannel: Canal:', channelName);
      console.log('useRealtimeChannel: Event:', event);
      console.log('useRealtimeChannel: Filter:', filter);
      console.log('useRealtimeChannel: Channel ref existe:', !!channelRef.current);
      console.log('useRealtimeChannel: Timestamp:', new Date().toISOString());
    }
    
    if (!channelRef.current) {
      console.warn('useRealtimeChannel: No hay canal para agregar listener');
      return null;
    }

    const result = channelRef.current.on(event, filter, callback);
    
    if (DEBUG) {
      console.log('useRealtimeChannel: Listener agregado exitosamente');
      console.log('useRealtimeChannel: Result:', result);
    }
    
    return result;
  }, [channelName]);

  // Función para enviar broadcasts
  const send = useCallback((event, payload) => {
    if (!channelRef.current) {
      console.warn('useRealtimeChannel: No hay canal para enviar broadcast');
      return;
    }

    return channelRef.current.send({
      type: 'broadcast',
      event,
      payload
    });
  }, [channelName]);

  // Función para obtener estado actual del canal
  const getChannelState = useCallback(() => {
    return {
      channel: channelRef.current,
      isSubscribed: isSubscribedRef.current,
      channelName,
      config: configRef.current
    };
  }, [channelName]);

  // Función para resetear el canal (limpiar y recrear)
  const reset = useCallback(() => {
    const DEBUG = process.env.NODE_ENV === 'development';
    
    if (DEBUG) {
      console.log('=== useRealtimeChannel: RESETEANDO CANAL ===');
      console.log('useRealtimeChannel: Canal:', channelName);
      console.log('useRealtimeChannel: Channel ref existe:', !!channelRef.current);
      console.log('useRealtimeChannel: Está suscrito:', isSubscribedRef.current);
      console.log('useRealtimeChannel: Timestamp:', new Date().toISOString());
    }
    
    if (channelRef.current) {
      if (DEBUG) {
        console.log('useRealtimeChannel: Removiendo canal existente');
      }
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
      isSubscribedRef.current = false;
      
      if (DEBUG) {
        console.log('useRealtimeChannel: Recreando canal inmediatamente');
      }
      // Recrear canal inmediatamente
      channelRef.current = supabase.channel(channelName, configRef.current);
      
      if (DEBUG) {
        console.log('useRealtimeChannel: Canal recreado exitosamente');
      }
    } else {
      if (DEBUG) {
        console.log('useRealtimeChannel: No hay canal para resetear');
      }
    }
  }, [channelName]);

  return {
    channel: channelRef.current,
    subscribe,
    on,
    send,
    getChannelState,
    reset,
    // Exponer métodos de debugging
    debug: {
      getChannelInfo: () => ({
        name: channelName,
        isSubscribed: isSubscribedRef.current,
        hasChannel: !!channelRef.current,
        config: configRef.current
      })
    }
  };
};

/**
 * Hook para gestionar múltiples canales con cleanup automático
 * Útil para componentes que necesitan varios canales simultáneos
 */
export const useMultipleRealtimeChannels = (channelConfigs = []) => {
  const channelsRef = useRef(new Map());
  const cleanupFunctionsRef = useRef([]);

  useEffect(() => {
    // Limpiar todos los canales existentes
    cleanupFunctionsRef.current.forEach(cleanup => cleanup());
    channelsRef.current.clear();
    cleanupFunctionsRef.current = [];

    // Crear nuevos canales
    channelConfigs.forEach(({ name, config }) => {
      if (!name) return;

      const channel = supabase.channel(name, config);
      channelsRef.current.set(name, channel);

      const cleanup = () => {
        if (channel) {
          supabase.removeChannel(channel);
          channelsRef.current.delete(name);
        }
      };

      cleanupFunctionsRef.current.push(cleanup);
    });

    return () => {
      // Cleanup de todos los canales
      cleanupFunctionsRef.current.forEach(cleanup => cleanup());
      channelsRef.current.clear();
      cleanupFunctionsRef.current = [];
    };
  }, [JSON.stringify(channelConfigs.map(c => ({ name: c.name, config: c.config })))]);

  const getChannel = useCallback((name) => {
    return channelsRef.current.get(name);
  }, []);

  const subscribeToChannel = useCallback((name, callback) => {
    const channel = channelsRef.current.get(name);
    if (!channel) {
      console.warn(`useMultipleRealtimeChannels: Canal ${name} no encontrado`);
      return;
    }

    channel.subscribe((status, err) => {
      if (callback) callback(status, err);
    });
  }, []);

  const sendToChannel = useCallback((name, event, payload) => {
    const channel = channelsRef.current.get(name);
    if (!channel) {
      console.warn(`useMultipleRealtimeChannels: Canal ${name} no encontrado para enviar`);
      return;
    }

    channel.send({
      type: 'broadcast',
      event,
      payload
    });
  }, []);

  return {
    getChannel,
    subscribeToChannel,
    sendToChannel,
    getAllChannels: () => Array.from(channelsRef.current.entries()),
    getChannelsCount: () => channelsRef.current.size
  };
};

export default useRealtimeChannel;
